/**
 * Shared formatting + period helpers for the Reports pages.
 * Period is month-granular everywhere: Current month / Previous month / Custom
 * (From-month -> To-month). Windows are always whole calendar months so the
 * month-keyed billingData rollups stay exact.
 */

/** Capitalize the first letter of a tenant label (e.g. "trilogy" -> "Trilogy"). */
export function capitalizeTenant(name) {
  const s = String(name || '').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Current calendar month key, yyyy-mm. */
export function currentYmKey(now = new Date()) {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** Previous calendar month key relative to a yyyy-mm. */
export function ymMinusOne(ym) {
  const [ys, ms] = String(ym).split('-').map(Number);
  let y = ys;
  let m = ms;
  if (m <= 1) {
    y -= 1;
    m = 12;
  } else {
    m -= 1;
  }
  return `${y}-${String(m).padStart(2, '0')}`;
}

/** Inclusive list of yyyy-mm keys from startYm..endYm (order-safe). */
export function ymKeysBetween(startYm, endYm) {
  let a = startYm;
  let b = endYm;
  if (a > b) {
    const t = a;
    a = b;
    b = t;
  }
  const out = [];
  let cursor = a;
  // Guard against runaway loops.
  for (let i = 0; i < 240 && cursor <= b; i += 1) {
    out.push(cursor);
    if (cursor === b) break;
    const [ys, ms] = cursor.split('-').map(Number);
    let y = ys;
    let m = ms + 1;
    if (m > 12) {
      y += 1;
      m = 1;
    }
    cursor = `${y}-${String(m).padStart(2, '0')}`;
  }
  return out;
}

/** Equal-length month block immediately preceding the given ordered ymKeys. */
export function priorBlockYmKeys(ymKeys) {
  if (!ymKeys?.length) return [];
  const n = ymKeys.length;
  const firstPrev = ymMinusOne(ymKeys[0]);
  // Build n keys ending at firstPrev.
  const keys = [firstPrev];
  let cursor = firstPrev;
  for (let i = 1; i < n; i += 1) {
    cursor = ymMinusOne(cursor);
    keys.unshift(cursor);
  }
  return keys;
}

/** Date bounds (local) for a yyyy-mm. */
function monthBounds(ym) {
  const [y, m] = String(ym).split('-').map(Number);
  const start = new Date(y, m - 1, 1, 0, 0, 0, 0);
  const end = new Date(y, m, 0, 23, 59, 59, 999);
  return { start, end };
}

/** Pretty single-month label, e.g. "January 2025". */
export function formatYmLabel(ym) {
  const [y, m] = String(ym).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return String(ym || '');
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** Range label across ymKeys, e.g. "January 2025" or "Jan 2025 – Mar 2025". */
export function formatPeriodLabel(ymKeys) {
  if (!ymKeys?.length) return '';
  if (ymKeys.length === 1) return formatYmLabel(ymKeys[0]);
  const fmtShort = (ym) => {
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };
  return `${fmtShort(ymKeys[0])} – ${fmtShort(ymKeys[ymKeys.length - 1])}`;
}

/**
 * Build the global period object consumed by every report.
 * @param {'thisMonth'|'lastMonth'|'custom'} preset
 * @param {string} fromYm yyyy-mm (custom only)
 * @param {string} toYm yyyy-mm (custom only)
 */
export function buildPeriod(preset, fromYm, toYm) {
  let ymKeys;
  if (preset === 'custom' && fromYm && toYm) {
    ymKeys = ymKeysBetween(fromYm, toYm);
  } else if (preset === 'lastMonth') {
    ymKeys = [ymMinusOne(currentYmKey())];
  } else {
    ymKeys = [currentYmKey()];
  }
  const { start } = monthBounds(ymKeys[0]);
  const { end } = monthBounds(ymKeys[ymKeys.length - 1]);
  return {
    preset,
    ymKeys,
    start,
    end,
    periodLabel: formatPeriodLabel(ymKeys),
  };
}

/** Human-readable duration from milliseconds. */
export function formatDurationMs(ms) {
  if (ms == null || Number.isNaN(ms) || ms < 0) return '—';
  if (ms < 90_000) return `${Math.round(ms / 1000)} sec`;
  if (ms < 3_600_000) return `${(ms / 60_000).toFixed(0)} min`;
  if (ms < 24 * 3_600_000) return `${(ms / 3_600_000).toFixed(1)} hr`;
  return `${(ms / (24 * 3_600_000)).toFixed(1)} days`;
}

/** Median of a numeric array (sorts a copy). */
export function median(nums) {
  if (!nums?.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/** Mean of a numeric array. */
export function mean(nums) {
  if (!nums?.length) return null;
  return nums.reduce((s, x) => s + x, 0) / nums.length;
}

/** CSV row escaping. */
export function toCsvRow(cells) {
  return cells
    .map((c) => {
      const t = c == null ? '' : String(c);
      if (/[",\n]/.test(t)) return `"${t.replace(/"/g, '""')}"`;
      return t;
    })
    .join(',');
}

/**
 * Open the browser print dialog with a temporary, meaningful document title so
 * the browser-injected print header (which we can't restyle) reads as e.g.
 * "Care Alerts - Baxter Health - June 2026" instead of the app name. The
 * original title is restored after printing.
 * @param {string} title
 */
export function printReport(title) {
  const prev = document.title;
  if (title) document.title = title;
  const restore = () => {
    document.title = prev;
    window.removeEventListener('afterprint', restore);
  };
  window.addEventListener('afterprint', restore);
  window.print();
}

/** Trigger a client-side CSV download. */
export function downloadCsv(filename, lines) {
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const el = document.createElement('a');
  el.href = url;
  el.download = filename;
  el.click();
  URL.revokeObjectURL(url);
}
